import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
} from '@tanstack/react-router'
import {
  deleteDeploymentFn,
  listDeploymentsFn,
} from '@/server/functions/deployments'
import { Button } from '@/components/ui/button'
import { Plus, Server, Cpu, RefreshCw, MoreVertical, Trash2, Wallet } from 'lucide-react'
import { useState, useEffect } from 'react'
import { type Deployments } from '@/server/lib/appwrite.types'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import { toast } from 'sonner'

export const Route = createFileRoute('/_protected/dashboard')({
  loader: async () => {

    const { deployments } = await listDeploymentsFn()

    return { deployments }
  },
  component: Dashboard,
})

function Dashboard() {
  const { deployments } = Route.useLoaderData()
  const navigate = useNavigate()
  const router = useRouter()
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Get user context and plan
  const { currentUser, credits } = Route.useRouteContext()
  const userPlan = currentUser?.prefs?.plan || 'starter'
  const hasAgents = deployments.length > 0
  const canCreateAgent = !(userPlan === 'starter' && hasAgents)

  useEffect(() => {
    if (localStorage.getItem('pending_deployment')) {
      navigate({ to: '/new' }).catch(() => {
        // toast.error("Failed to redirect to new deployment")
      })
    }
  }, [navigate])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await router.invalidate() // Invalidate router to re-run loaders
    setTimeout(() => setIsRefreshing(false), 1000)
  }

  const handleDelete = async (id: string) => {
    try {
      if (!confirm('Are you sure you want to delete this specific database?'))
        return

      await deleteDeploymentFn({ data: { id } })
      toast.success('Deployment deleted successfully')
      await router.invalidate()
    } catch {
      toast.error('Failed to delete deployment')
    }
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Dashboard Navbar */}


      <main className="container mx-auto max-w-6xl px-6 pt-10 pb-12">
        {/* Unified Command Center Banner */}
        <div className="relative mb-10 animate-fade-in">
          {/* Main Banner */}
          <div className="relative overflow-hidden rounded-[2rem] bg-slate-900 border border-slate-800 p-6 md:p-8 shadow-2xl">
            {/* Background decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4 pointer-events-none" />

            <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
              {/* Left: User & Summary */}
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center text-white shadow-lg shadow-primary/20 shrink-0">
                  <span className="text-2xl font-black">
                    {currentUser?.name?.charAt(0) || 'U'}
                  </span>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
                    Welcome, {currentUser?.name?.split(' ')[0] || 'Explorer'}
                  </h1>
                  <p className="text-slate-400 text-sm flex items-center gap-2">
                    <span className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      {
                        deployments.filter(
                          (d: Deployments) => d.status === 'running',
                        ).length
                      }{' '}
                      Active Agents
                    </span>
                    <span className="text-slate-700">•</span>
                    <span>Global Infrastructure Operational</span>
                  </p>
                </div>
              </div>

              {/* Right: Actions & Stats */}
              <div className="flex flex-wrap items-center gap-4 md:gap-8 lg:border-l lg:border-slate-800 lg:pl-8">
                <div className="flex gap-x-8 gap-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1">
                      Remaining Credits
                    </p>
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex items-center gap-2">
                        <Cpu className="w-4 h-4 text-primary" />
                        <span className="text-xl font-mono text-white font-bold">
                          {credits}
                        </span>
                      </div>
                      <Link to="/billing">
                        <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10">
                          <Wallet className="w-3 h-3 mr-1" />
                          Top Up
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-black text-slate-500 mb-1">
                      Current Tier
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-white/10 text-white text-[10px] font-bold uppercase tracking-tight border border-white/10">
                        {currentUser?.prefs?.plan || 'Starter'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="h-10 w-px bg-slate-800 hidden md:block"></div>

                <div className="flex items-center gap-3 w-full sm:w-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="text-slate-400 hover:text-white hover:bg-white/5"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
                    />
                  </Button>

                  {canCreateAgent ? (
                    <Link to="/new" className="flex-1 sm:flex-none">
                      <Button className="w-full bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 h-11 px-6 rounded-xl font-bold">
                        <Plus className="w-4 h-4 mr-2" />
                        New Agent
                      </Button>
                    </Link>
                  ) : (
                    <div className="flex-1 sm:flex-none relative group">
                      <Button
                        disabled
                        className="w-full bg-primary/50 hover:bg-primary/50 text-white/70 shadow-lg shadow-primary/20 h-11 px-6 rounded-xl font-bold cursor-not-allowed opacity-[.7]"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Agent
                      </Button>
                      {/* Tooltip hint */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-md shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                        Starter plan allows only 1 agent. Upgrade to create
                        more.
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-900"></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Deployments List */}
        <div
          className="space-y-4 animate-fade-up"
          style={{ animationDelay: '100ms' }}
        >
          <h2 className="text-lg font-semibold mb-4">Your Instances</h2>

          {deployments.length === 0 ? (
            <div className="text-center py-20 bg-muted/30 rounded-xl border border-dashed border-border">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Server className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">No deployments yet</h3>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                Create your first AI instance to get started with QuickClaw.
              </p>
              <Link to="/new">
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Instance
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid gap-4">
              {deployments.map((deployment: Deployments) => (
                <div
                  key={deployment.$id}
                  className="group flex flex-col md:flex-row md:items-center justify-between p-5 rounded-xl bg-card border border-border hover:border-primary/30 transition-all shadow-sm hover:shadow-md"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-start gap-4 mb-4 md:mb-0">
                      <div
                        className={`mt-1 w-2.5 h-2.5 rounded-full ${deployment.status === 'running'
                          ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]'
                          : deployment.status === 'failed'
                            ? 'bg-red-500'
                            : deployment.status === 'removing'
                              ? 'bg-red-500 animate-pulse'
                              : 'bg-blue-500 animate-pulse'
                          }`}
                      />

                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">
                            {deployment.name}
                          </h3>
                          <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                            {deployment.region}
                          </span>
                          {deployment.status !== 'running' && (
                            <span
                              className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${deployment.status === 'failed' || deployment.status === 'removing' ? 'bg-red-500/10 text-red-500' : 'bg-yellow-500/10 text-yellow-500'}`}
                            >
                              {deployment.status}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <Cpu className="w-3.5 h-3.5" />2 vCPU · 4 GB RAM ·
                            10 GB SSD
                          </span>
                          <div className="flex items-center gap-2 border-l border-border pl-4">
                            <span className="text-xs font-medium">
                              Connected:
                            </span>
                            {deployment.telegramToken ? (
                              <img
                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/8/82/Telegram_logo.svg/960px-Telegram_logo.svg.png"
                                className="w-4 h-4 hover:scale-110 transition-transform"
                                alt="Telegram"
                                title="Telegram"
                              />
                            ) : (
                              <span className="text-xs text-muted-foreground/50">
                                None
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-4 md:mt-0">
                    {deployment.status === 'verifying' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-yellow-500/50 text-yellow-600 hover:bg-yellow-500/10 h-8 text-xs font-bold"
                        onClick={() => {
                          localStorage.setItem(
                            'active_deployment_id',
                            deployment.$id,
                          )
                          void navigate({
                            to: '/new',
                            search: { resume: 'true' },
                          })
                        }}
                      >
                        Complete Setup
                      </Button>
                    )}
                    {deployment.status !== 'removing' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleDelete(deployment.$id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Button>

                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </Button>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
